import { createApi } from '@reduxjs/toolkit/dist/query/react';

import serverRoutes from '@app/utils/server-routes';

import {
  CreateDashboardWidgetType,
  DashboardWidgetListObject,
  DashboardWidgetObject,
  DashboardWidgetsLayoutRequestType,
  EditDashboardWidgetType,
} from '@app/interfaces/dashboard-widgets.type';

import { ChartsLayoutType } from '@app/interfaces/dashboards.type';

import { baseQuery } from '../baseQuery';

export const dashboardWidgetsApi = createApi({
  reducerPath: 'dashboardWidgetsApi',
  tagTypes: ['widgetsList', 'singleWidget'],
  baseQuery: baseQuery,
  endpoints: (build) => ({
    getDashboardWidgetsList: build.query<
      { dashboard_widget_list: DashboardWidgetListObject; initialLayout: ChartsLayoutType[] },
      { dashboard_id: string }
    >({
      query: (params) => ({
        url: serverRoutes.dashboard_widget_bulk,
        params,
      }),
      transformResponse: (baseQueryReturnValue: {
        dashboard_widget_list: DashboardWidgetListObject;
      }) => {
        const layout: ChartsLayoutType[] = baseQueryReturnValue.dashboard_widget_list.map(
          ({ layout, dashboard_widget_id }) => ({ ...layout, i: dashboard_widget_id }),
        );
        return {
          dashboard_widget_list: baseQueryReturnValue.dashboard_widget_list,
          initialLayout: layout,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.dashboard_widget_list.map(({ dashboard_widget_id }) => ({
                type: 'widgetsList' as const,
                dashboard_widget_id,
              })),
              'widgetsList',
            ]
          : ['widgetsList'],
    }),
    getSingleDashboardWidgets: build.query<DashboardWidgetObject, { dashboard_widget_id: string }>({
      query: (params) => ({
        url: serverRoutes.dashboard_widget,
        params,
      }),
      providesTags: ['singleWidget'],
    }),
    createDashboardWidget: build.mutation<DashboardWidgetObject, CreateDashboardWidgetType>({
      query: (body) => ({
        url: serverRoutes.dashboard_widget,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['widgetsList'],
    }),
    editDashboardWidget: build.mutation<DashboardWidgetObject, EditDashboardWidgetType>({
      query: ({ body, params }) => ({
        url: serverRoutes.dashboard_widget,
        method: 'PUT',
        body,
        params,
      }),
      invalidatesTags: ['widgetsList', 'singleWidget'],
    }),
    deleteDashboardWidget: build.mutation<DashboardWidgetObject, { dashboard_widget_id: string }>({
      query: (params) => ({
        url: serverRoutes.dashboard_widget,
        method: 'DELETE',
        params,
      }),
      invalidatesTags: ['widgetsList'],
    }),
    changeDashboardWidgetLayout: build.mutation<
      DashboardWidgetObject,
      { params: { dashboard_id: string }; body: DashboardWidgetsLayoutRequestType }
    >({
      query: ({ params, body }) => ({
        url: serverRoutes.dashboard_widget_layout,
        method: 'PUT',
        params,
        body,
      }),
      invalidatesTags: ['widgetsList'],
    }),
  }),
});

export const {
  useGetDashboardWidgetsListQuery,
  useLazyGetSingleDashboardWidgetsQuery,
  useCreateDashboardWidgetMutation,
  useEditDashboardWidgetMutation,
  useDeleteDashboardWidgetMutation,
  useChangeDashboardWidgetLayoutMutation,
} = dashboardWidgetsApi;
